"use client";

import React, { createContext, useContext, useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Player, PredictionMatchResult, Stage } from '@repo/types';
import { useCompetitionContext } from './CompetitionContext';
import { toMillionPrice } from '../lib/utils';

import { toast } from 'react-toastify';

type Owner = {
  id: number | null;
  firstName?: string | null;
  lastName?: string | null;
  name: string;
}

type RosterPlayer = {
  id: number;
  bon: number;
  cap: number;
  pos: number;
  val: string;
  week: number;
}

type Match = {
  id: number;
  index: number;
  stage: 'qualifying' | 'race';
}

type ExtraInfo = {
  racePosition: string;
  sprintPosition: string;
  qualifyingSprintPosition: string;
  qualifyingPosition: string;
}

type Roster = {
  players: RosterPlayer[][];
  matches: Match[][][];
  extra: ExtraInfo[][];
}

interface Extra {
  qualifyingPosition: string;
  racePosition: string;
  qualifyingSprintPosition: string;
  sprintPosition: string;
}

interface BaseTeam {
  competitionFeed: string;
  seasonId: number;
  teamName: string;
  starting: Player[];
  bench: Player[];
  prediction: PredictionMatchResult[];
  extra: Extra;
}

// Initial team state (before we have a teamId)
interface InitialTeam extends BaseTeam {
  id?: number;
  teamId: null;
  userId: null;
  weekId?: null;
  points?: number | null;
  rank?: number | null;
  roster?: any;
  leagues?: any;
  stats?: any;
  tripleCaptain?: any;
}

// Full team state (after we have loaded from server)
interface FullTeam extends BaseTeam {
  id: number;
  teamId: number;
  name: string;
  userId: number;
  weekId: number;
  budget: string;
  value: string;
  points: number | null;
  rank: number | null;
  leagueWeek: number | null;
  owner: Owner;
  roster: Roster;
  leagues: any;
  stats?: any;
  transfers?: any;
  tripleCaptain: any;
  // boosters?: any;
}

// Union type for all possible team states
type Team = InitialTeam | FullTeam;

interface TransformedPrediction {
  id: number;
  stage: Stage;
  index: number;
}

interface TransformedTeam {
  competitionFeed: string;
  seasonId: number;
  teamName: string;
  starting: number[];
  bench: number[];
  prediction: TransformedPrediction[];
  extra: Extra;
  teamId?: number | null;
}

interface TeamContextType {
  team: Team;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  saveTeam: () => void;
  updateTeam: (teamId: string) => void;
  fetchTeam: (teamId: string) => void;
  driverSelectionCheck: (driver: Player, selectionStage?: Stage, transferChanges?: { inId?: Player; outId: Player }[], isTransferMode?: boolean, effectiveAvailableBudget?: number) => string;
  addDriverToTeam: (driver: Player) => void;
  removeDriverFromTeam: (driver: Player) => void;
  updateLineup: (starting: Player[], bench: Player[]) => void;
  addDriverToPrediction: (driver: Player, result: number, selectionStage: Stage ) => void;
  removeDriverFromPrediction: (driver: Player, selectionStage: Stage, result?: number) => void;
  updateExtraPrediction: (selectionStage: Stage, result?: string) => void;
  availableBudget: number;
  setTeamName: (name: string) => void;
  setCaptain: (driver: Player) => void;
  updateForWeek: (newWeekId: number) => void;
}

import { CompetitionData } from '@repo/types'; 

interface TeamProviderProps {
  children: React.ReactNode;
  teamId?: string | null;
  withPoints?: boolean;
  competitionData: CompetitionData;
  currentWeekId: number;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const useTeamContext = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeamContext must be used within a TeamProvider');
  }
  return context;
};

const fetchTeamFromAPI = async (teamId: string): Promise<FullTeam> => {
  const response = await fetch(`/api/team/${teamId}`);
  if (!response.ok) throw new Error('Failed to fetch team');
  return response.json();
};

const fetchTeamPointsFromAPI = async (teamId: string): Promise<FullTeam> => {
  const response = await fetch(`/api/team/${teamId}`);
  if (!response.ok) throw new Error('Failed to fetch team points');
  return response.json();
};

const saveTeamToAPI = async (team: TransformedTeam): Promise<{ team: Team; message?: string }> => {
  const response = await fetch('/api/team', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({...team, newsletterAccepted: true}),
  });
  
  if (!response.ok) {
    // Create a proper error object that will be caught by the mutation's onError handler
    let errorMessage = `Error ${response.status}: ${response.statusText}`;
    
    // Clone the response since we might need to try multiple read methods
    const clonedResponse = response.clone();
    
    // First try to read as JSON (most APIs return errors in JSON format)
    try {
      const errorData = await response.json();
      
      // Check various common error message formats
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.details || errorData.errors) {
          // Handle validation errors which might be in an array
          const details = errorData.details || errorData.errors;
          if (Array.isArray(details) && details.length > 0) {
            errorMessage = details.map(d => d.message || d).join(', ');
          } else {
            errorMessage = JSON.stringify(details);
          }
        }
      }
    } catch (jsonError) {
      // If JSON parsing fails, try to get text content
      try {
        const textContent = await clonedResponse.text();
        if (textContent && textContent.length < 500) { // Only use text if reasonably short
          errorMessage = textContent;
        }
      } catch (textError) {
        // Fallback to the default error message if both attempts fail
        console.error('Failed to parse error response:', textError);
      }
    }
    
    throw new Error(errorMessage);
  }
  
  return response.json();
};

const updateTeamToAPI = async (
  team: TransformedTeam,
  weekId: number
): Promise<{ success: boolean; message?: string; data: { teamId: number; roster: Roster } }> => {
  const response = await fetch(`/api/team/${team.teamId}/week/${weekId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(team),
  });
  
  if (!response.ok) {
    // Create a proper error object that will be caught by the mutation's onError handler
    let errorMessage = `Error ${response.status}: ${response.statusText}`;
    
    // Clone the response since we might need to try multiple read methods
    const clonedResponse = response.clone();
    
    // First try to read as JSON (most APIs return errors in JSON format)
    try {
      const errorData = await response.json();
      
      // Check various common error message formats
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.details || errorData.errors) {
          // Handle validation errors which might be in an array
          const details = errorData.details || errorData.errors;
          if (Array.isArray(details) && details.length > 0) {
            errorMessage = details.map(d => d.message || d).join(', ');
          } else {
            errorMessage = JSON.stringify(details);
          }
        }
      }
    } catch (jsonError) {
      // If JSON parsing fails, try to get text content
      try {
        const textContent = await clonedResponse.text();
        if (textContent && textContent.length < 500) { // Only use text if reasonably short
          errorMessage = textContent;
        }
      } catch (textError) {
        // Fallback to the default error message if both attempts fail
        console.error('Failed to parse error response:', textError);
      }
    }
    
    throw new Error(errorMessage);
  }
  
  return response.json();
};

export function transformTeam(originalTeam: Team): TransformedTeam {
  const { competitionFeed, seasonId, teamId, teamName, extra, starting, bench, prediction } = originalTeam;

  // Extract starting player IDs
  const startingIds = starting.map(player => player.id);

  // Extract bench player IDs
  const benchIds = bench.map(player => player.id);

  // Transform predictions
  const transformedPredictions: TransformedPrediction[] = [];

  prediction.forEach(pred => {
    if (pred.stage === 'qualifying' || pred.stage === 'race' || pred.stage === 'sprint' || pred.stage === 'qualifyingsprint') {
      transformedPredictions.push({
        id: pred.id,
        stage: pred.stage,
        index: pred.index || 0
      });
    }
  });

  return {
    competitionFeed,
    seasonId,
    teamId,
    teamName,
    starting: startingIds,
    bench: benchIds,
    prediction: transformedPredictions,
    extra
  };
}

export function deTransformTeam(fetchedTeam: any, competitionData: CompetitionData, weekId?: number): Team {
  // const weekId = competitionData.deadlineInfo?.deadlineWeek || 0;//todo currentWeekId
  const selectedWeekId = weekId || competitionData.deadlineInfo?.deadlineWeek || 0;
  // Week 22 is the bonus round: it scores the lineup locked for week 21
  // and therefore has no separate roster entry of its own.
  const rosterWeekId = selectedWeekId === 22 ? 21 : selectedWeekId;

  // If we don't have players or a valid fetchedTeam, return a default initial-like team
  if (!competitionData.players || !fetchedTeam.roster) {
    return {
      teamId: null,
      userId: null,
      competitionFeed: fetchedTeam?.competitionFeed || '',
      seasonId: fetchedTeam?.seasonId || 0,
      teamName: '',
      starting: [],
      bench: [],
      prediction: [],
      extra: {
        qualifyingPosition: '0',
        racePosition: '0',
        qualifyingSprintPosition: '0',
        sprintPosition: '0'
      }
    };
  }

  // Resolve which week's roster actually holds the selections. The requested
  // week can be empty (e.g. a week before the team existed), so fall back to the
  // team's own week and finally to the first populated week so selections are
  // never silently dropped.
  const rosterPlayersByWeek = fetchedTeam.roster.players || [];
  const weekHasPlayers = (week: number) =>
    Array.isArray(rosterPlayersByWeek[week]) && rosterPlayersByWeek[week].length > 0;

  let resolvedWeekId = rosterWeekId;
  if (!weekHasPlayers(resolvedWeekId)) {
    if (weekHasPlayers(fetchedTeam.weekId)) {
      resolvedWeekId = fetchedTeam.weekId;
    } else {
      const firstPopulatedWeek = rosterPlayersByWeek.findIndex(
        (weekRoster: any) => Array.isArray(weekRoster) && weekRoster.length > 0
      );
      if (firstPopulatedWeek !== -1) {
        resolvedWeekId = firstPopulatedWeek;
      }
    }
  }

  // Collect current week's players by matching fetchedTeam.roster.players with competitionData.players
  const weekPlayers = (rosterPlayersByWeek[resolvedWeekId] || []).reduce((acc: Player[], rosterPlayer: any) => {
    const matchedPlayer = competitionData.players?.find((player) => player.id === rosterPlayer.id);
    if (matchedPlayer) {
      acc.push({
        ...matchedPlayer,
        pos: rosterPlayer.pos,
        bon: rosterPlayer.bon,
        cap: rosterPlayer.cap,
        val: rosterPlayer.val,
        week: rosterPlayer.week
      });
    }
    return acc;
  }, []);

  const starting = weekPlayers.filter((p: any) => p.pos === 1);
  const bench = weekPlayers.filter((p: any) => p.pos === 0);

  const prediction:any = [];
  competitionData.players?.forEach((player) => {
    if(fetchedTeam.roster.matches?.[resolvedWeekId]?.[0]?.length > 0) {
      fetchedTeam.roster.matches[resolvedWeekId][0].forEach((match: any) => {
        if (player.id === match.id) {
          prediction.push({
            ...player,
            stage: match.stage,
            index: match.index
          });
        }
      });
    } else {
      console.log('we do not have selections here');
    }
  })

  const weekExtra = ((fetchedTeam.roster.extra || [])[resolvedWeekId] || [])[0] || {
    qualifyingPosition: '0',
    racePosition: '0',
    qualifyingSprintPosition: '0',
    sprintPosition: '0'
  };

  return {
    ...fetchedTeam,
    teamName: fetchedTeam.teamName || fetchedTeam.name,
    starting,
    bench,
    prediction,
    extra: {
      qualifyingPosition: weekExtra.qualifyingPosition,
      racePosition: weekExtra.racePosition,
      qualifyingSprintPosition: weekExtra.qualifyingSprintPosition,
      sprintPosition: weekExtra.sprintPosition
    }
  };
}

const getTeamQueryKey = (teamId: string | number | null | undefined, withPoints: boolean = false) =>
  ['team', String(teamId ?? ''), withPoints ? 'points' : 'detail'] as const;

const useTeamQuery = (teamId: string | null | undefined, withPoints: boolean = false) => {
  const { competitionConfig } = useCompetitionContext();
  
  return useQuery({
    queryKey: getTeamQueryKey(teamId, withPoints),
    queryFn: async () => {
      if (!teamId) {
        const initialTeam: InitialTeam = {
          teamId: null,
          userId: null,
          competitionFeed: competitionConfig.competitionFeed,
          seasonId: competitionConfig.seasonId,
          teamName: '',
          starting: [],
          bench: [],
          prediction: [],
          extra: {
            qualifyingPosition: '0',
            racePosition: '0',
            qualifyingSprintPosition: '0',
            sprintPosition: '0'
          }
        };
        return initialTeam;
      }
      
      if (withPoints) {
        return fetchTeamPointsFromAPI(teamId);
      }
      return fetchTeamFromAPI(teamId);
    },
    // Turn off automatic refetch on window focus:
    refetchOnWindowFocus: false,
    // Optionally turn off refetch on reconnect too:
    refetchOnReconnect: false,
  });
};

export const TeamProvider: React.FC<TeamProviderProps> = ({ children, teamId, withPoints = false, competitionData, currentWeekId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { competitionConfig } = useCompetitionContext();

  const initialTeam: InitialTeam = useMemo(() => ({
    competitionFeed: competitionConfig.competitionFeed,
    seasonId: competitionConfig.seasonId,
    teamId: null,
    userId: null,
    teamName: '',
    starting: [],
    bench: [],
    prediction: [],
    extra: {
      qualifyingPosition: '0',
      racePosition: '0',
      qualifyingSprintPosition: '0',
      sprintPosition: '0'
    },
    tripleCaptain: null
  }), [competitionConfig.competitionFeed, competitionConfig.seasonId]);

  const [team, setTeam] = useState<Team>(initialTeam);
  const teamQueryKey = useMemo(() => getTeamQueryKey(teamId, withPoints), [teamId, withPoints]);

  const { data: fetchedTeam, isPending: isLoading, isError, refetch } = useTeamQuery(teamId, withPoints);

  React.useEffect(() => {  
    if (!fetchedTeam) return;

    if (fetchedTeam.weekId) {
      // Always de-transform so `starting`/`bench` are derived from the roster.
      // When the viewed week is before the team existed (or currentWeekId isn't
      // ready yet, e.g. still 0 on first load), fall back to the team's own week
      // so its selections are still resolved instead of leaving them undefined.
      const targetWeekId = currentWeekId && currentWeekId >= fetchedTeam.weekId
        ? currentWeekId
        : fetchedTeam.weekId;
      const deTransformedTeam = deTransformTeam(fetchedTeam, competitionData, targetWeekId);
      setTeam(deTransformedTeam);
    } else {
      setTeam(fetchedTeam);
    }
  }, [fetchedTeam, competitionData, currentWeekId, initialTeam]);

  const updateForWeek = (newWeekId: number) => {
    // Get the most up-to-date team data from cache
    const cachedTeamData = queryClient.getQueryData(teamQueryKey);
    // console.log(typeof teamId);

    // console.log('cachedTeamData', cachedTeamData)
    
    const sourceTeamData = cachedTeamData || team;
    if (sourceTeamData) {
      // Transform the data for the new week
      const transformedDataForNewWeek = deTransformTeam(
        sourceTeamData,
        competitionData,
        newWeekId
      );
      
      // Update the state with the transformed data for the new week
      // console.log('transformedDataForNewWeek', transformedDataForNewWeek)
      setTeam(transformedDataForNewWeek);
    }
  };

  const { mutate: saveTeamMutation, status } = useMutation({
    mutationFn: saveTeamToAPI,
    onSuccess: (savedTeam) => {
      queryClient.setQueryData(getTeamQueryKey(savedTeam.team.teamId), savedTeam.team);
            //todo untested fix queryClient.getQueryData(getTeamQueryKey(savedTeam.team.teamId))
      setTeam(savedTeam.team);
      toast.success(savedTeam.message || 'Equipe enregistree avec succes');
      //navigate(`/fantasy/edit/${savedTeam.team.teamId}`);
      window.location.href = '/fantasy';
    },
    onError: (error: any) => {
      // Extract error message if available
      const errorMessage = error.message || "Une erreur s'est produite lors de l'enregistrement de votre equipe";
      toast.error(errorMessage);
    }
  });
  const isSaving = status === 'pending';

  const saveTeam = () => {
    saveTeamMutation(transformTeam(team));
  };

  const { mutate: updateTeamMutation } = useMutation({
    mutationFn: ({ team, weekId }: { team: TransformedTeam, weekId: number }) => updateTeamToAPI(team, weekId),
    onSuccess: (savedTeam) => {
      const updatedTeam = {
        ...team,
        roster: savedTeam.data.roster
      };

      queryClient.setQueryData(getTeamQueryKey(savedTeam.data.teamId), updatedTeam);
      queryClient.getQueryData(getTeamQueryKey(savedTeam.data.teamId))

      const transformedDataForCurrentWeek = deTransformTeam(
        updatedTeam, 
        competitionData, 
        currentWeekId
      );

      setTeam(transformedDataForCurrentWeek);
      
      toast.success(savedTeam.message || 'Equipe enregistree avec succes');
    },
    onError: (error: Error) => {
      // Use the error message that was extracted from the API response
      const errorMessage = error.message || "Une erreur s'est produite lors de l'enregistrement de votre equipe";
      toast.error(errorMessage);
    }
  });
  
  const updateTeam = (teamId: string) => {
    team.teamId = parseInt(teamId);
    updateTeamMutation({ team: transformTeam(team), weekId: currentWeekId });
  };

  // Define a fetchTeam function that will force a refetch from the server
  const fetchTeam = (id: string) => {
    // Invalidate the current team query data to force a fresh fetch
    queryClient.invalidateQueries({ queryKey: getTeamQueryKey(id, withPoints) });
    // Trigger the refetch immediately
    refetch();
  };

  const availableBudget = useMemo(() => {
    if(!team.starting || !team.bench) {
      return competitionConfig.budget * 1000000;
    }
    const totalUsed = [...team.starting, ...team.bench].reduce((acc, driver) => {
      const price = toMillionPrice(driver.value);
      return acc + price;
    }, 0);
    return (competitionConfig.budget * 1000000) - totalUsed;
  }, [team.starting, team.bench, competitionConfig.budget]);

  const driversPerClub = useMemo(() => {
    if(!team.starting || !team.bench) {
      return [null];
    }
    const counts = [...team.starting, ...team.bench].reduce((acc: { [key: number]: number }, driver) => {
      if (driver.clubId) {
        acc[driver.clubId] = (acc[driver.clubId] || 0) + 1;
      }
      return acc;
    }, {});
    return counts;
  }, [team.starting, team.bench]);

  const driverSelectionCheck = (driver: Player, selectionStage?: Stage, transferChanges?: { inId?: Player; outId: Player }[], isTransferMode?: boolean, effectiveAvailableBudget?: number): string => {
    
    if(transferChanges?.some(change => change.inId?.id === driver.id || change.outId.id === driver.id)) {
      return 'transferList'
    }

    // console.log('selectionStage', selectionStage, team.prediction, 'transferChanges', transferChanges)

    if (selectionStage) {
      if (team.prediction.find(d => d.id === driver.id && d.stage === selectionStage)) {
        return 'removable';
      }
      return 'selectable';
    }

    if ([...team.starting, ...team.bench].find(d => d.id === driver.id) && !(transferChanges?.length && competitionConfig.transferTaxPercentage > 0 && driver.active === 1)) {
      return 'removable';
    }

    if (driver.clubId && (driversPerClub[driver.clubId] || 0) >= competitionConfig.teamSameClubPlayersLimit) {
      return 'clubLimit';
    }

    const rawValue = driver?.value;
    const numericValue = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue ?? 0;
    const transferTax = isTransferMode && competitionConfig.transferTaxPercentage ? numericValue*10000*competitionConfig.transferTaxPercentage : 0;
    const calculatedDriverValue = isTransferMode && competitionConfig.transferTaxPercentage ? numericValue*1000000 + transferTax : numericValue*1000000;
    
    // console.log(driver.name, isTransferMode, calculatedDriverValue, availableBudget);

    const budget = isTransferMode ? (effectiveAvailableBudget || 0) : availableBudget;

    return (calculatedDriverValue <= budget && [...team.starting, ...team.bench].length < (competitionConfig.lineupSize + competitionConfig.benchSize))
      ? 'selectable'
      : calculatedDriverValue > budget ? 'overBudget' : '';
  };

  const addDriverToTeam = (driver: Player) => {
    // First check if driver is already on the team
    if (team.starting.find(d => d.id === driver.id)) return;
    if (team.bench.find(d => d.id === driver.id)) return;
  
    // Check if driver is selectable
    if (driverSelectionCheck(driver) !== 'selectable') return;
  
    // Try to add to starting lineup if there's space
    if (team.starting.length < competitionConfig.lineupSize) {
      setTeam({
        ...team,
        starting: [...team.starting, driver]
      });
    }
    // If starting is full, try to add to bench if there's space
    else if (team.bench.length < competitionConfig.benchSize) {
      setTeam({
        ...team,
        bench: [...team.bench, driver]
      });
    }
    // If both starting and bench are full, throw an error
    else {
      throw new Error('Team is full - cannot add more drivers');
    }
  };

  const removeDriverFromTeam = (driver: Player) => {
    // Removes both from starting and bench
    setTeam({
      ...team,
      starting: team.starting.filter(d => d.id !== driver.id),
      bench: team.bench.filter(d => d.id !== driver.id)
    });
  };

  const updateLineup = (starting: Player[], bench: Player[]) => {
    setTeam({
      ...team,
      starting,
      bench
    });
  };

  const addDriverToPrediction = (driver: Player, result: number, selectionStage: Stage) => {
    if (team.prediction.find(d => d.id === driver.id && d.stage === selectionStage)) return;
    setTeam({
      ...team,
      prediction: [...team.prediction, { ...driver, stage: selectionStage, index: result }]
    });
  };

  const removeDriverFromPrediction = (driver: Player, selectionStage: Stage, result?: number) => {
    setTeam({
      ...team,
      prediction: team.prediction.filter(d => !(d.id === driver.id && d.stage === selectionStage))
    });
  };

  const setTeamName = (name: string) => {
    setTeam({ ...team, teamName: name });
  };

  //TODO will not work with Football (use Roster)
  const setCaptain = (captain: Player) => {
    // If already captain, do nothing.
    if (team.starting.length > 0 && team.starting[0]?.id === captain.id) return;
    
    // Check if the driver is already in the starting lineup.
    const startingIndex = team.starting.findIndex(d => d.id === captain.id);
    if (startingIndex !== -1) {
      // Swap the chosen driver with the first driver.
      const newStarting = [...team.starting];
      const temp = newStarting[0]!;
      newStarting[0] = newStarting[startingIndex]!;
      newStarting[startingIndex] = temp;

      // Update the captain flag on all drivers
      const updatedStarting = newStarting.map(driver => ({
        ...driver,
        cap: driver.id === captain.id ? 1 : 0
      }));
      
      setTeam({ ...team, starting: updatedStarting });
    }
  };  

  const updateExtraPrediction = (selectionStage: Stage, result?: string) => {
    const newExtra = { ...team.extra };
    switch (selectionStage) {
      case 'qualifying':
        newExtra.qualifyingPosition = result || '0';
        break;
      case 'qualifyingsprint':
        newExtra.qualifyingSprintPosition = result || '0';
        break;
      case 'sprint':
        newExtra.sprintPosition = result || '0';
        break;
      case 'race':
        newExtra.racePosition = result || '0';
        break;
    }

    setTeam({ ...team, extra: newExtra });
  };

  return (
    <TeamContext.Provider
      value={{
        team,
        isLoading,
        isError,
        isSaving,
        saveTeam,
        updateTeam,
        fetchTeam,
        // fetchTeam: (id: string) => queryClient.invalidateQueries({ queryKey: ['team', id] }),
        driverSelectionCheck,
        addDriverToTeam,
        removeDriverFromTeam,
        updateLineup,
        addDriverToPrediction,
        removeDriverFromPrediction,
        availableBudget,
        updateExtraPrediction,
        setTeamName,
        setCaptain,
        updateForWeek
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};
